import React, { useState, useEffect, useContext } from "react"
import { SnackNotificationContext } from 'legacy-components/notifications/GenericSnackNotification';
import { ProductDetailModel, useProductDetailsController } from "./ProductDetails.controller"
import GenericPageTitle from "modus/components/generic-page-title/GenericPageTitle"
import { Box, Button, Grid, Paper, makeStyles, Theme } from "@material-ui/core"
import { SubscriptionNavigator } from "pages/home/subscriptions/components/subscription-navigator/SubscriptionNavigator"
import { ProductServices } from "../ProductServices"
import ManageAPIsDrawer from "../ManageAPIsDrawer";
import { Skeleton } from "@material-ui/lab"
import { withViewController } from "modus/utilities/ViewController"
import { useHistory, useLocation } from "react-router"
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import DataRemover from "modus/components/misc/DataRemover";
import { useAsyncAction } from "legacy-components/form/action/AsyncAction";
import { from } from "rxjs";
import { FormattedMessage } from "react-intl"
import Alert from "@material-ui/lab/Alert"
import UnifiedAPIService, { UnifiedAPI } from "services/UnifiedAPIService"
import { useConfirm } from "material-ui-confirm"
import Authorizer, { useAuthorizer } from "modus/components/authorizer/Authorizer";
import { useSelector } from "react-redux";
import { MY_TEAM_KEY } from "header/team-selector/TeamSelector";
import {AuthorizationProvider, useAuthorization} from "../../../authorizer/components/AuthorizationProvider";
import { ProductEditor } from "../product-form/ProductEditor";

const useStyles = makeStyles((theme: Theme) => ({
  editButton: {
    textTransform: 'none',
    marginLeft: '20px',
    marginRight: '10px',
    borderColor: '#0063A3',
    color: '#0063A3'
  },
  deleteButton: {
    marginLeft: '10px',
    backgroundColor: "#DA212C",
    color: "#FFFFFF",
  },
  deleteButtonDisabled: {
    marginLeft: '10px',
    backgroundColor: "#D9D9D9",
    color: "#FFFFFF",
    cursor: 'not-allowed'
  },
  messageBox: {
    marginTop: "30px"
  },
  emptyStateContainer: {
    minHeight: 300,
    border: '2px solid #D9D9D9',
    borderRadius: 4,
    background: '#fff'
  },
  emptyStateText: {
    fontSize: 16,
    color: "#464B52"
  },
  addApiButton: {
    fontWeight: 600,
    fontSize: 18,
    padding: '20px',
    borderRadius: 6
  },
  plusIcon: {
    fontSize: 24,
    fontWeight: 700,
    marginRight: 4
  },
  skeletonContainer: {
    marginTop: "30px"
  },
  inlineBlock: {
    display: 'inline-block'
  },
  cursorNotAllowed: {
    cursor: 'not-allowed'
  },
  cursorPointer: {
    cursor: 'pointer'
  },
  fullWidth: {
    width: '100%'
  },
  badgesContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: '8px',
    marginLeft: '16px'
  },
}));

export const ProductDetailsViewer:React.FC<ProductDetailModel> = ({response, subscribe}) => {
  const classes = useStyles();
  const notification = useContext(SnackNotificationContext);
  
  // Custom styles for the visibility badge
  const visibilityBadgeStyle = {
    backgroundColor: "#FFC107", // Yellow color
    color: "#000000", // Black text for better contrast on yellow
    padding: "4px 8px",
    borderRadius: "12px",
    fontSize: "11px",
    fontWeight: 600,
    textTransform: "uppercase" as const,
    letterSpacing: "1px"
  };
  // State for ManageAPIsDrawer when no APIs exist
  const [manageAPIsOpen, setManageAPIsOpen] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const confirm = useConfirm();
  const [openEditForm, setOpenEditForm] = useState<boolean>(false);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [showDelete, setShowDelete] = useState(false);
  const teamId = useSelector((state: any) =>state?.teamManager?.teams?.currentTeam?.id ?? localStorage.getItem(MY_TEAM_KEY));
  // useAsyncAction for product delete
  const [deleteState, { subscribe: deleteSubscribe }] = useAsyncAction(() => from(
    UnifiedAPIService.delete(
      UnifiedAPI.parseRequestURL(UnifiedAPI.delete_product_api, {
        productName: response.value?.data?.name
      })
    )
  ));

  const openSubscriptionPage = () => {
    history.push(`/home/consumer/products/${response.value?.data.id}/subscribe`);
  };

  const handleOpenEditForm = () => {
    setOpenEditForm(true);
  };

  const handleCloseEditForm = () => {
    setOpenEditForm(false);
  };

  // Effect to handle notifications and navigation after delete
  useEffect(() => {
    if (!deleteState) return;
    if (deleteState.error) {
      notification.show({
        id: 'product-details-error',
        message: <span>{deleteState.value as any ?? deleteState.error}</span>,
        timeout: 4000,
      });
    }
    if (deleteState.loaded) {
      notification.show({
        id: 'product-details-success',
        message: <span>Product has been deleted</span>,
        timeout: 4000,
      });
      setTimeout(() => {
        setShowDelete(false);
        history.push("/home/consumer/products/list");
      }, 1000);
    }
  }, [deleteState, history]);

  const handleProductUpdate = async (updatedProduct: any) => {
    handleCloseEditForm();
    const prevVisibility = (response.value?.data?.visibility ?? "")
      .toString()
      .toLowerCase()
      .trim();
    const newVisibility = (updatedProduct?.visibility ?? "")
      .toString()
      .toLowerCase()
      .trim();
    const visibilityChanged = prevVisibility !== newVisibility;

    notification.show({
      id: 'product-details-success',
      message: <span>Product "{updatedProduct.name}" successfully updated</span>,
      timeout: 4000,
    });
    if (visibilityChanged && newVisibility) {
      const productDisplayName = (
        response.value?.data?.displayName ??
        response.value?.data?.name ??
        updatedProduct.name ??
        ""
      ).trim();
      history.replace({
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
        state: {
          ...(location.state as Record<string, unknown> | undefined),
          productVisibilityUpdated: true,
          visibilityUpdateProductName: productDisplayName,
          visibilityUpdateNewValue: newVisibility,
        },
      });
    }
    setIsRefreshing(true);
    try {
      if (subscribe) {
        subscribe(false);
      } else {
        setIsRefreshing(false);
      }
    } catch (error) {
      setIsRefreshing(false);
      notification.show({
        id: 'product-refresh-failed',
        message: <span>Failed to refresh product data. Please try again.</span>,
        timeout: 4000,
      });
    }
  };

  // Add: handleAPIsUpdate for ManageAPIsDrawer
  const handleAPIsUpdate = () => {
    notification.show({
      id: 'product-details-success',
      message: <span>APIs successfully updated</span>,
      timeout: 4000,
    });
    setIsRefreshing(true);
    try {
      if (subscribe) {
        subscribe(false);
      } else {
        setIsRefreshing(false);
      }
    } catch (error) {
      setIsRefreshing(false);
      notification.show({
        id: 'product-refresh-failed',
        message: <span>Failed to refresh product data. Please try again.</span>,
        timeout: 4000,
      });
    }
  };

  // Effect to reset refreshing state when data is loaded
  useEffect(() => {
    if (response?.loaded && isRefreshing) {
      setIsRefreshing(false);
    }
  }, [response?.loaded, isRefreshing]);

  const { isActionAllowed } = useAuthorization("PRODUCT" , response?.value?.data?.id || "none");
  const { hasPermission: hasApiProductsUpdate } = useAuthorizer("api_products", "update");
  const { hasPermission: hasApiProductsMakePublic } = useAuthorizer("api_products", "make_public");
  /** Edit Product: PRODUCT update/make_public, or api_products update/make_public. */
  const canEditProduct =
    isActionAllowed("update") ||
    isActionAllowed("make_public") ||
    hasApiProductsUpdate ||
    hasApiProductsMakePublic;
  /** Owning team required unless role has api_products make_public (cross-team edit). */
  const showEditProductButton =
    canEditProduct &&
    (hasApiProductsMakePublic || response?.value?.data?.teamId === teamId);
  const canUpdate = isActionAllowed("update");
  const canDelete = isActionAllowed("delete");

  return (
        <>
      {response.loaded && response?.value?.data && (
        <>
        <AuthorizationProvider
            resources={[
              {
                resourceType: "PRODUCT",
                resourceId: response?.value?.data?.id || ""
              },
            ]}
            resourceType="PRODUCT"
        >
          <GenericPageTitle
            title={
              <Box display="flex" alignItems="center">
                <span>{response?.value?.data?.displayName ?? response.value?.data?.name}</span>
                <Box className={classes.badgesContainer}>
                  {/* Visibility badge - using custom styling instead of StatusBadge */}
                  {response.value?.data?.visibility && (
                    <Box style={visibilityBadgeStyle}>
                      {response.value.data.visibility.toUpperCase()}
                    </Box>
                  )}
                </Box>
              </Box>
            }
            description={
              <>
                {response?.value?.data?.longDescription}
              </>
            }
            controls={
              <>
              <Authorizer resource="subscriptions" action="create">
              <SubscriptionNavigator openSubscriptionPage={openSubscriptionPage}/>
              </Authorizer>
              { showEditProductButton &&  (<Button
              startIcon={<EditIcon />}
              onClick={handleOpenEditForm}
              variant="outlined"
              color="primary"
              className={classes.editButton}
            >
              <FormattedMessage defaultMessage="Edit Product" />
            </Button>)}

              {/* Wrap DataRemover in a Tooltip to show tooltipText even when disabled */}
              <Box component="span" className={classes.inlineBlock}>
                {  response?.value?.data?.teamId === teamId &&  (
                <Box
                  component="span"
                  title={response?.value?.data?.hasSubscriptions === true
                    ? "Product with subscriptions cannot be deleted"
                    : response?.value?.data?.isProxiesMapped === true
                    ? "Deleting the product will unmap the proxies associated with it."
                    : ""}
                  className={(response?.value?.data?.hasSubscriptions === true || response?.value?.data?.hasSubscriptions === undefined) ? classes.cursorNotAllowed : classes.cursorPointer}
                >
                  {canDelete &&
                  <DataRemover
                    className={response?.value?.data?.hasSubscriptions === true ? classes.deleteButtonDisabled : classes.deleteButton}
                    confirmationText={response.value?.data?.name}
                    enterButtonText={"Delete Product"}
                    onConfirm={deleteSubscribe}
                    asyncActionState={deleteState}
                    tooltipText={response?.value?.data?.hasSubscriptions === true ? "Product with subscriptions cannot be deleted" : response?.value?.data?.isProxiesMapped === true ? "Deleting the product will unmap the proxies associated with it." : "Delete this Product"}
                    title="Delete Product"
                    successMessage="Deleted Successfully"
                    onSuccess={() => setShowDelete(false)}
                    disabled={(response?.value?.data?.hasSubscriptions === true || response?.value?.data?.hasSubscriptions === undefined)}
                    icon={<DeleteIcon />}
                    message={
                      <Box>
                        <Box className={classes.messageBox}>
                          This action cannot be undone. This will permanently remove the product "{response.value?.data?.name}" and cannot be accessed elsewhere.
                          <br/>
                        </Box>
                      </Box>
                    }
                  />}
                </Box>) }
              </Box>
              </>
            }
            noSubTitle
          />

          <ProductEditor
          product={response.value?.data}
            open={openEditForm}
            onClose={handleCloseEditForm}
            onSubmit={handleProductUpdate}
          />
          {/* Subtle refreshing indicator when the data is being updated */}
          {isRefreshing && (
            <Box position="fixed" top={10} right={10} zIndex={9}>
              <Alert severity="info" variant="filled">
                <Box display="flex" alignItems="center">
                  <Box mr={1}>Refreshing product data...</Box>
                  <Skeleton variant="text" width={40} animation="wave" />
                </Box>
              </Alert>
            </Box>
          )}

          {response.value?.data?.versions && response.value?.data?.versions.length > 0 ? (
            <ProductServices
              productId={response.value?.data?.id || ""}
              productName={response.value?.data?.name}
              versions={response.value?.data?.versions}
              visibility={response.value?.data?.visibility ?? undefined}
              scope={response.value?.data?.scope ?? undefined}
              onUpdate={handleAPIsUpdate}
            />
          ) : (
            <Box mt={4} mb={4} display="flex" flexDirection="column" alignItems="center" justifyContent="center" className={classes.emptyStateContainer}>
              {
                canUpdate ?
                    <Box mb={2} className={classes.emptyStateText}>
                      No APIs have been added to this product. Please add an API to proceed.
                    </Box>
                    :
                    <Box mb={2} className={classes.emptyStateText}>
                      No APIs have been added to this product.
                    </Box>
              }
            { response?.value?.data?.teamId === teamId &&  (
              <Authorizer resource="apis" action="create">
                {canUpdate && <Button
                variant="outlined"
                color="primary"
                className={classes.addApiButton}
                onClick={() => setManageAPIsOpen(true)}
                startIcon={<span className={classes.plusIcon}>+</span>}
              >
                Add New API
              </Button>}

                <ManageAPIsDrawer
                open={manageAPIsOpen}
                onClose={() => setManageAPIsOpen(false)}
                productId={response.value?.data?.name}
                productVisibility={response.value?.data?.visibility ?? undefined}
                onUpdate={handleAPIsUpdate}
              />
              </Authorizer>)}
            </Box>
          )}
        </AuthorizationProvider>
        </>
      )}
      {!response.loaded && (
        <>
          <GenericPageTitle noControls noSubTitle/>
          <Grid container>
            <Grid item md={6} xs={12}>
              <Box py={2}>
                <Skeleton
                  animation="wave"
                  variant="rect"
                  height={"56px"}
                  width="100%"
                />
              </Box>
            </Grid>
          </Grid>
          <Paper className={classes.skeletonContainer}>
            <Box p={2} className={classes.fullWidth}>
              <Skeleton
                animation="wave"
                variant="rect"
                height={"200px"}
                width="100%"
              />
            </Box>
          </Paper>
        </>
      )}

    </>
    );
}

export const ProductDetails = withViewController(ProductDetailsViewer, useProductDetailsController);